import React, { useState, useRef } from "react";
import { Header } from "../Forms/SmartForm";
import Modal from "../Modal/Modal";
import ModalGate from "../ModalGate/ModalGate";
import axios from "axios";

const PdfSubmission = ({ article = {} }) => {
  const [displayResponseModal, setDisplayResponseModal] = useState(false);
  const [relatedOLP, setRelatedOLP] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const cardListRef = useRef(null);
  const cardListArrowRef = useRef(null);
  const eventListenersAddedRef = useRef(false);

  const addScrollEventListeners = () => {
    const slider = cardListRef.current;
    const arrow = cardListArrowRef.current;
    const isListening = eventListenersAddedRef.current;

    // check the event listeners have not already been added
    if (!slider || isListening) return;

    // Scrollbars can behave differently across various browsers and devices, here we implementing a custom drag-to-scroll feature
    let isDown = false;
    let startX;
    let scrollLeft;

    const onMouseDown = (e) => {
      isDown = true;
      slider.classList.add("active");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    };

    const onMouseLeave = () => {
      isDown = false;
      slider.classList.remove("active");
    };

    const onMouseUp = () => {
      isDown = false;
      slider.classList.remove("active");
    };

    const cardWidth = slider.querySelector(".pdfRelatedContent .articleTab").offsetWidth;
    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 3;
      slider.scrollLeft = scrollLeft - walk;
    };

    slider.addEventListener("mousedown", onMouseDown);
    slider.addEventListener("mouseleave", onMouseLeave);
    slider.addEventListener("mouseup", onMouseUp);
    slider.addEventListener("mousemove", onMouseMove);

    if (arrow) {
      arrow.addEventListener("click", () => {
        // Smooth scroll to the new position by one card width
        slider.scrollLeft += cardWidth;
      });
    }
    eventListenersAddedRef.current = true;

    // Cleanup function
    return () => {
      slider.removeEventListener("mousedown", onMouseDown);
      slider.removeEventListener("mouseleave", onMouseLeave);
      slider.removeEventListener("mouseup", onMouseUp);
      slider.removeEventListener("mousemove", onMouseMove);

      if (arrow) {
        arrow.removeEventListener("click");
      }
    };
  };

  const decodeSpecialCharacters = (html) => {
    const element = document.createElement("div");
    element.innerHTML = html;
    return element.textContent;
  };

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`/api/v2/pdf-generator/${article.articleNo}`, {
        formValues
      });
      setRelatedOLP(response.data.relatedOLP);
      addScrollEventListeners();
      // Push datalayer event for tracking within Google Analytics (GTM)
      window.dataLayer.push({
        event: "submitPDFFormSuccess",
        gtmCustomClick: {
          eventCategory: "Conversion"
        }
      });
    } catch (error) {
      setErrorMessage("An error occurred while generating the PDF. Please try again later.");
      // The errors are caught in web server and logged to rollbar
      // TODO: Use Logger
    }
    setDisplayResponseModal(true);
    setIsSubmitting(false);
  };

  const handleDataLayer = (articleClicked, index) => {
    if (window.dataLayer) {
      const dataLayer = {
        event: "olp-related-content-click-pdf-modal",
        index: index,
        articleTitle: articleClicked.title,
        sponsoredVendorNos: articleClicked.sponsoredVendorNos.join(","),
        sponsorNames: articleClicked.sponsorNames.join(","),
        clickUrl: articleClicked.absoluteURL
      };
      window.dataLayer.push(dataLayer);
    }
  };

  const modalHeaderContent = () => (
    <>
      <p className="m-b-0 text-inverted">Complete the form below and we will email you a PDF version of</p>
      <p
        className="text-inverted bold"
        dangerouslySetInnerHTML={{
          __html: article.title
        }}
      ></p>
    </>
  );

  const renderRelatedArticles = () => {
    if (relatedOLP && relatedOLP.length > 0) {
      return (
        <div className="pdfRelatedContent p-1">
          <h2>You might also be interested in...</h2>
          <div className="articleTabList" ref={cardListRef}>
            {relatedOLP.map((item, index) => {
              const { id, title, aImgPictureTag, absoluteURL } = item;
              return (
                <div className="articleTab forMedia" key={id}>
                  {aImgPictureTag && (
                    <div className="articleTabImg">
                      <a href={absoluteURL} target="_blank" rel="noopener" onClick={() => handleDataLayer(item, index)}>
                        <picture>
                          {aImgPictureTag.sourceTagData.map((data, index) => {
                            return <source key={index} media={data.media} srcSet={data.srcset} type={data.type} />;
                          })}
                          <img
                            className={aImgPictureTag.imageTagData.class}
                            src={aImgPictureTag.imageTagData.src}
                            alt={aImgPictureTag.imageTagData.alt}
                            width={400}
                            height={200}
                            title={aImgPictureTag.imageTagData.title}
                          ></img>
                        </picture>
                      </a>
                    </div>
                  )}
                  <div className="articleTabText">
                    <a href={absoluteURL} target="_blank" rel="noopener" onClick={() => handleDataLayer(item, index)}>
                      <h3 className="max-3-lines">{decodeSpecialCharacters(title)}</h3>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="card-list-arrow" ref={cardListArrowRef}>
            {">"}
          </div>
        </div>
      );
    }
    return null;
  };

  const ResponseModal = () => {
    return (
      <Modal>
        <div className="modal-gate" id="ResponseModal">
          {errorMessage ? (
            <div className="modal-gate-content background-white p-b-1 flex flex-col flex-align-center">
              <Header className="p-v-1"></Header>
              <div className="m-2 text-center">
                <p>{errorMessage}</p>
                <p>
                  If the issue persists, contact{" "}
                  <a className="text-link underline" href={`mailto:info@labmanager.com`}>
                    info@clinicallab.com
                  </a>{" "}
                  for support.
                </p>
              </div>
              <button className="action-btn" onClick={() => setDisplayResponseModal(false)}>
                <span style={{ marginRight: "5px" }}>Close </span> <i className="fas fa-times fa-lg" />
              </button>
            </div>
          ) : (
            <div className="modal-gate-content large m-0">
              <button
                className="close-btn corner background-transparent no-border text-white float-right"
                onClick={() => setDisplayResponseModal(false)}
              >
                <i className="fas fa-times fa-lg" />
              </button>
              <Header className="p-v-1 p-h-2 m-b-0 text-center">
                <h1 className="text-inverted">Thank you!</h1>
                <p className="text-inverted">We have emailed you a PDF version of the article you requested.</p>
              </Header>
              {renderRelatedArticles()}
              <div className="form-footer background-white text-center p-v-1 p-h-2">
                <h1>Can't find the email?</h1>
                <p>Please check your spam or junk folder.</p>
                <p>
                  You can also add{" "}
                  <a className="email" href="mailto:info@clinicallab.com">
                    info@clinicallab.com
                  </a>{" "}
                  to your safe senders list to ensure that you never miss a message from us.
                </p>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  const modalGateTrigger = ({ type, id, onClick, dataAttribute = "", disabled = false, isSubmitting }) => (
    <button
      type={type}
      id={id}
      className="action-btn"
      data-attribute={dataAttribute}
      onClick={onClick}
      disabled={disabled}
    >
      <div key={isSubmitting ? "spinner" : "pdfIcon"}>
        {isSubmitting ? (
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
        ) : (
          <i className="far fa-file-pdf" aria-hidden="true" data-attribute={dataAttribute} />
        )}
      </div>
      <span data-attribute={dataAttribute}>Download PDF</span>
    </button>
  );

  return (
    <div id="PdfSubmission">
      {displayResponseModal && ResponseModal()}
      <ModalGate
        onSubmit={onSubmit}
        dataAttribute="get-pdf-version"
        modalHeaderContent={modalHeaderContent}
        initialSubmitting={isSubmitting}
        closeForm={displayResponseModal}
        modalGateTrigger={modalGateTrigger}
        clickOpenModalEventName="clickOpenPDFModal"
        clickCloseModalEventName="clickClosePDFModal"
      />
    </div>
  );
};

export default PdfSubmission;
